import {
  Button,
  Checkbox,
  CheckboxLabel,
  FormField,
  Input,
  Item,
  ItemActions,
  ItemLabel,
  ItemList,
  Label,
  MiniButton,
  SearchInput,
} from '@/components/ui-primitives/small'
import React, { useMemo, useRef, useState } from "react"
import { v4 as uuidv4 } from 'uuid'
import { mergeClasses } from "@/helpers/styling"
import { Copy, Pause, Play, Plus, Trash, Zap } from "lucide-react"
import { CodeEditor } from "@/components/editor/code-editor"
import { ResizablePanel, ResizablePanelGroup } from "@/components/ui-primitives/resizable"
import { parseSparqlParams } from "@/utils/parse-sparql-params"
import { useFloatingState } from "@/components/editor/panels/panel-registry"
import { saveViewerSnapshot } from "@/components/editor/widget-editor/undo-redo"
import { ViewerConfig } from "@/components/Viewer"
import { produce } from "immer"
import { PanelChrome } from "@/components/editor/panels/panel-chrome"
import invariant from "tiny-invariant"

export type Trigger = {
  id: string
  label: string
  sparqlQuery: string
  voteForUserActionsDisabledWhenLoading: boolean
}

export function TriggerEditor(props: {
  viewerConfig: ViewerConfig
  onChange: (viewer: ViewerConfig) => void
}) {
  const containerRef = useRef<HTMLUListElement>(null)
  const [ selectedId, setSelectedId ] = useState<string | null>(null)
  const [ searchTerm, setSearchTerm ] = useState("")
  const trigger = props.viewerConfig?.triggers?.find(t => t.id === selectedId)
  const floatingState = useFloatingState()

  const scrollTriggerIntoView = (newId: string) => {
    const newTriggerRow = containerRef.current?.querySelector(`[data-trigger-id="${newId}"]`)
    newTriggerRow?.scrollIntoView({ behavior: "instant" })
  }

  const addTrigger = () => {
    const newId = uuidv4()
    const newViewer = produce(props.viewerConfig, (draft: ViewerConfig) => {
      if (!draft.triggers) draft.triggers = []
      const newTrigger = createDefaultTriggerDraft(newId)
      draft.triggers.push(newTrigger)
    })
    // happy-todo: make this nicer
    props.onChange(newViewer)
    saveViewerSnapshot(newViewer, "add new trigger")
    setSelectedId(newId)
    setTimeout(() => scrollTriggerIntoView(newId))
  }

  const removeTrigger = (removeId: string) => {
    const newViewer = produce(props.viewerConfig, (draft: ViewerConfig) => {
      const triggers = draft.triggers
      if (!triggers) throw new Error("should have triggers at this point")
      const removeIndex = triggers.findIndex(t => t.id === removeId)
      triggers.splice(removeIndex, 1)
    })
    // happy-todo: make this nicer
    props.onChange(newViewer)
    saveViewerSnapshot(newViewer, "remove trigger")
  }

  const editTrigger = (edit: Partial<Trigger>) => {
    if (selectedId == null) return
    const newViewer = produce(props.viewerConfig, (draft: ViewerConfig) => {
      const triggers = draft.triggers
      if (!triggers) throw new Error("should have triggers at this point")
      const selectedIndex = triggers.findIndex(t => t.id === selectedId)
      triggers[selectedIndex] = {
        ...triggers[selectedIndex],
        ...edit
      }
    })
    // happy-todo: make this nicer
    props.onChange(newViewer)
    saveViewerSnapshot(newViewer, "edit trigger " + Object.keys(edit).join(", "))
  }

  const dupeTrigger = (sourceId: string) => {
    const newId = uuidv4()
    const newViewer = produce(props.viewerConfig, (draft: ViewerConfig) => {
      const triggers = draft.triggers
      if (!triggers) throw new Error("should have triggers at this point")
      const source = triggers.find(t => t.id === sourceId)
      invariant(source, "Trigger to copy should exist")
      const copy = { ...source, id: newId }
      triggers.push(copy)
    })
    props.onChange(newViewer)
    saveViewerSnapshot(newViewer, "duplicate trigger")
    setSelectedId(newId)
  }

  const triggersFilteredSorted = useMemo(() => {
    if (!props.viewerConfig.triggers) return []
    return props.viewerConfig.triggers.filter(trigger => {
      return !searchTerm
        || trigger.label.toLowerCase().includes(searchTerm.toLowerCase())
        || trigger.sparqlQuery.toLowerCase().includes(searchTerm.toLowerCase())
    }).sort((a, b) => {
      const aParams = parseSparqlParams(a.sparqlQuery)
      const bParams = parseSparqlParams(b.sparqlQuery)
      const aLabel = a.label || aParams.join(' ')
      const bLabel = b.label || bParams.join(' ')
      return aLabel.localeCompare(bLabel)
    })
  }, [ props.viewerConfig.triggers, searchTerm ])

  return (
    <ResizablePanelGroup className="max-h-full"
                         orientation={floatingState === "docked" ? "vertical" : "horizontal"}>
      <ResizablePanel className="flex flex-col">
        <div className="flex gap-2 p-2 flex-shrink-0 border-b border-border">
          <SearchInput placeholder="Search triggers..."
                       value={searchTerm}
                       onChange={setSearchTerm} />
          <Button variant="outline"
                  className="h-8"
                  title="Add trigger"
                  onClick={addTrigger}>
            <Plus size={14} /> Add
          </Button>
        </div>
        <div className="flex-grow border-b border-gray-200 min-h-0 overflow-y-auto bg-white" style={{
            scrollbarWidth: "thin"
          }}>
          <ItemList ref={containerRef}>
            {triggersFilteredSorted.map(trigger => {
              const isSelected = selectedId === trigger.id
              const params = parseSparqlParams(trigger.sparqlQuery)
              const isLabelSameAsParam = params[0] === trigger.label
              return (
                <Item selected={isSelected}
                      data-trigger-id={trigger.id}
                      onClick={() => setSelectedId(trigger.id)}>
                  <ItemLabel>
                    {isSelected
                      ? <Zap size={11} className="min-w-[11px]" fill="black" />
                      : <Zap size={11} className="min-w-[11px] opacity-50 group-hover:opacity-100" />
                    }
                    <div className={mergeClasses("flex gap-1 min-w-0 items-center", {
                      "flex-colx": isSelected
                    })}>
                      {(trigger.label && !isLabelSameAsParam) && (
                        <div className="flex-shrink-0 min-w-0 max-w-full items-center">
                          <div className="font-medium truncate">
                            {trigger.label}
                          </div>
                        </div>
                      )}
                      <div className="flex-1 min-w-0 font-light text-indigo-700 truncate">
                        {params.map(p => `?${p}`).join(' ')}
                      </div>
                    </div>
                  </ItemLabel>
                  <ItemActions>
                    <MiniButton title="Remove"
                                onClick={() => removeTrigger(trigger.id)}>
                      <Trash size={12} />
                    </MiniButton>
                    <MiniButton title="Make copy"
                                onClick={() => dupeTrigger(trigger.id)}>
                      <Copy size={12} />
                    </MiniButton>
                  </ItemActions>
                </Item>
              )
            })}
          </ItemList>
        </div>
      </ResizablePanel>
      <ResizablePanel defaultSize={floatingState === "docked" ? "400px" : "50%"}>
        {trigger
          ? <TriggerForm trigger={trigger}
                         editTrigger={editTrigger} />
          : (
            <div className="flex flex-col h-full items-center justify-center">
              <div className="my-3 text-gray-500">Select a trigger, or</div>
              <Button variant="outline" onClick={addTrigger}>
                <Plus size={14} /> Add new
              </Button>
            </div>
          )}
      </ResizablePanel>
    </ResizablePanelGroup>
  )
}

function TriggerForm(props: {
  trigger: Trigger
  editTrigger: (edit: Partial<Trigger>) => void
}) {
  return (
    <div className="flex h-full flex-col gap-2 p-2">
      <FormField id="trigger_label">
        <Label>
          <span className="flex-grow">Label</span>
          <span className="opacity-50">optional</span>
        </Label>
        <Input type="text"
               value={props.trigger.label}
               onChange={e => props.editTrigger({ label: e.target.value })}/>
      </FormField>
      <FormField id="trigger_sparql" className="flex-grow">
        <Label>SPARQL query</Label>
        <CodeEditor mode="sparql"
                    value={props.trigger.sparqlQuery}
                    onValueChange={value => props.editTrigger({ sparqlQuery: value })}
                    autoFocus
                    fullHeight
                    publishValueChangeOnBlur />
      </FormField>
      <FormField id="trigger_voteForUserActionsDisabledWhenLoading">
        <CheckboxLabel>
          <Checkbox
            checked={props.trigger.voteForUserActionsDisabledWhenLoading}
            onClick={() => props.editTrigger({ voteForUserActionsDisabledWhenLoading: !props.trigger.voteForUserActionsDisabledWhenLoading })} />
          Block user interaction
        </CheckboxLabel>
      </FormField>
    </div>
  )
}

export function TriggersStartStopButtons(props: {
  paused: boolean
  togglePaused: () => void
}) {
  return ( // align fix
    <div className="my-[-2px] flex items-center gap-0.5">
      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
        {props.paused
          ? <span className="opacity-75">paused</span>
          : <span className="animate-pulse">running...</span>}
      </div>
      <PanelChrome.HeaderIcon
        title="Pause trigger execution"
        className={mergeClasses({
          "text-red-700": !props.paused
        })}
        icon={Pause}
        iconSize={15}
        onClick={props.togglePaused}
        disabled={props.paused}
      />
      <PanelChrome.HeaderIcon
        title="Resume trigger execution"
        className={mergeClasses({
          "text-green-700": props.paused
        })}
        icon={Play}
        iconSize={15}
        svgAttributes={props.paused ? { fill: "currentColor" } : {}}
        onClick={props.togglePaused}
        disabled={!props.paused}
      />
    </div>
  )
}

function createDefaultTriggerDraft(id: string) {
  return {
    id,
    label: '',
    sparqlQuery: '',
    voteForUserActionsDisabledWhenLoading: false
  }
}
